import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCreditCard, FiLock, FiAlertCircle, FiCheck, FiShield } from 'react-icons/fi';
import { stripeConnectService } from '../services/stripeConnectService';

const StripeCheckout = ({ isOpen, onClose, shed, dealer, paymentType = 'deposit', customerInfo }) => {
  const [email, setEmail] = useState(customerInfo?.email || '');
  const [name, setName] = useState(customerInfo?.name || '');
  const [phone, setPhone] = useState(customerInfo?.phone || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isDeposit = paymentType === 'deposit';
  const amount = isDeposit
    ? (shed?.depositAmount || Math.round((shed?.salePrice || shed?.price) * 0.25))
    : (shed?.salePrice || shed?.price);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !name) {
      setError('Name and email are required');
      return;
    }

    if (!dealer?.stripe_connected) {
      setError('This dealer has not set up payment processing yet. Please contact them directly.');
      return;
    }

    setLoading(true);

    try {
      const result = await stripeConnectService.checkout({
        shed,
        dealer,
        customerEmail: email,
        customerName: name,
        customerPhone: phone,
        paymentType
      });

      if (result.error) {
        setError(result.error.message || 'Payment failed. Please try again.');
        setLoading(false);
      }
      // If successful, Stripe redirects — no need to handle here
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <FiCreditCard className="w-5 h-5 text-gray-700" />
              <h2 className="text-lg font-semibold text-gray-900">
                {isDeposit ? 'Reserve with Deposit' : 'Complete Purchase'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Shed Summary */}
          <div className="p-5 bg-gray-50 border-b border-gray-100">
            <div className="flex gap-4">
              {shed?.images?.[0] && (
                <img
                  src={shed.images[0]}
                  alt={shed.name}
                  className="w-20 h-20 rounded-lg object-cover"
                />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate">{shed?.name}</h3>
                <p className="text-sm text-gray-500">{shed?.size} &middot; {shed?.shedType}</p>
                {dealer?.company_name && (
                  <p className="text-xs text-gray-400 mt-1">Sold by {dealer.company_name}</p>
                )}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {isDeposit ? 'Deposit amount' : 'Total price'}
              </span>
              <span className="text-xl font-semibold text-gray-900">{formatPrice(amount)}</span>
            </div>
            {isDeposit && (
              <p className="text-xs text-gray-400 mt-1">
                Full price: {formatPrice(shed?.salePrice || shed?.price)} &middot; Remaining balance due at delivery
              </p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <p className="text-xs text-gray-400 mt-1">Receipt will be sent to this address</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                <FiAlertCircle className="flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gray-900 hover:bg-gray-800 disabled:bg-gray-400 text-white font-medium py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Redirecting to Stripe...
                </>
              ) : (
                <>
                  <FiLock className="w-4 h-4" />
                  Pay {formatPrice(amount)}
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-4 text-xs text-gray-400 pt-1">
              <span className="flex items-center gap-1">
                <FiShield className="w-3.5 h-3.5" />
                Secure checkout
              </span>
              <span className="flex items-center gap-1">
                <FiCreditCard className="w-3.5 h-3.5" />
                Powered by Stripe
              </span>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StripeCheckout;
